'use client';

import { MapComponents } from '@components/BlockMapper';
import ShopsNewestListings from '@components/CardGridWithMobileCarousel/ShopsNewestListings';
import InteriorHero from '@components/Hero/InteriorHero';
import NeighborhoodLocalLogic from '@components/NeighborhoodLocalLogic';
import StackedTextImageForm from '@components/TextAndImageBlock/StackedTextImageForm';
import IntroTextBlock from '@components/TextBlock';
import { getDefaultNeighborhoodContactUsComponentData } from '@data/neighborhoods';
import {
  Listing,
  ListingSearchResult,
  ListingSortKeys,
  LocalLogicCountry,
  SortDir,
  StandardStatusEnum,
} from '@engelvoelkersamericas/api';
import { AxiosResponse } from 'axios';
import { useAction } from '@util/customHooks/useAction';
import { Typography } from '@mui/material';
import { useJsApiLoader } from '@react-google-maps/api';
import { addShopDataToContentBlocks } from '@util/addShopDataToContentBlocks';
import { GMAPS_LIBRARIES } from '@util/customHooks/useLocationToGeoCode';
import getShopUrlPrepend from '@util/getShopUrlPrepend';
import { geocodeLocation } from '@util/location';
import { apiClient } from 'api-client';
import { useTranslations } from 'next-intl';
import { FC, useEffect, useState } from 'react';
import { MapSection } from '@components/PropertiesSearch/styles';
import PropertyListingsMap from '@components/PropertyListingsMap/PropertyListingsMap';

const config = {
  google: {
    mapKey: process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY!,
  },
};

export interface NeighborhoodHomepageProps {
  pageData: any;
  chimeData?: any;
  shopIds?: string;
  contentfulTagIds?: string;
  locale: string;
}

export interface LatLng {
  lat: number;
  lng: number;
}

const NeighborhoodHomepage: FC<NeighborhoodHomepageProps> = ({
  pageData,
  chimeData,
  shopIds,
  locale,
  contentfulTagIds,
}) => {
  const t = useTranslations();
  const fields = pageData?.fields;
  const shopUrlPrepend = getShopUrlPrepend();
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: config.google.mapKey,
    libraries: GMAPS_LIBRARIES,
  });
  const [neighborhoodError, setNeighborhoodError] = useState<string | null>(null);
  const [listingsError, setListingsError] = useState<string | null>(null);
  const [kmlPolygon, setKmlPolygon] = useState<LatLng[] | null>(null);

  //if there is contact us override, then use those props to render the StackedTextImageForm component
  //if not, then use the default component, which is entryId of 2kIbstc6fCkEEfjEzpT5Vu
  const [contactUsProps, setContactUsProps] = useState<any | null>(null);
  const { start, data: listingData, pending } = useAction<ListingSearchResult>();

  //
  //
  // data for the NewestListings and NeighborhoodLocalLogic APIs
  //
  const localLogicId = fields?.neighborhood?.fields?.id ? fields?.neighborhood?.fields?.id : null;
  const country = fields?.neighborhood?.fields?.country
    ? fields?.neighborhood?.fields?.country
    : null;
  const localLogicCountry = fields?.neighborhood?.fields?.country
    ? fields?.neighborhood?.fields?.country === 'United States'
      ? LocalLogicCountry.Usa
      : fields?.neighborhood?.fields?.country === 'Canada'
        ? LocalLogicCountry.Canada
        : null
    : null;
  const slug: string | null = fields?.neighborhood?.fields?.slug
    ? fields?.neighborhood?.fields?.slug
    : null;
  const [cardArr, setCardArr] = useState<Listing[] | null>(null);
  const [neighborhoodCentroid, setNeighborhoodCentroid] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [neighborhoodUrlParameters, setNeighborhoodUrlParameters] = useState<string | null>(null);
  const [neighborhoodStoryHeader, setNeighborhoodStoryHeader] = useState(
    fields?.storyContentHeader || null,
  );
  const [neighborhoodStoryBody, setNeighborhoodStoryBody] = useState(
    fields?.storyContentBody || null,
  );
  const [neighborhoodSubtitle, setNeighborhoodSubtitle] = useState(fields?.subtitle || null);

  const fetchNeighborhoodData = async (geogId: string, country: LocalLogicCountry) => {
    try {
      const res = await apiClient.getLocalLogicNeighborhoodById({
        id: geogId,
        country: country,
        geography: true,
        demographics: true,
        schools: false,
        pois: false,
      });

      const profile = res.data.profile?.data;

      // get data for neighborhood story
      if (profile) {
        if (!fields?.storyContentHeader && profile.intro) {
          setNeighborhoodStoryHeader(profile.intro);
        }
        if (!fields?.storyContentBody && profile.profiles?.character) {
          setNeighborhoodStoryBody(profile.profiles?.character);
        }
      }

      const urlParameters = `?neighborhoodId=${geogId}&country=${country}`;
      setNeighborhoodUrlParameters(urlParameters);

      // get data for neighborhood centroid
      if (profile?.centroid) {
        setNeighborhoodCentroid({
          lat: profile.centroid?.lat,
          lng: profile.centroid?.lng,
        });
      } else {
        setNeighborhoodCentroid(null);
      }
    } catch (err) {
      setNeighborhoodError('Error fetching neighborhood data');
      console.error(err);
    }
  };

  const fetchNeighborhoodListings = async (geogId: string, country: LocalLogicCountry) => {
    try {
      const listingsRes = await apiClient.getListingsByNeighborhood({
        listingFilters: {
          shopIds: shopIds?.split(','),
          listingStatuses: [StandardStatusEnum.Active],
        },
        id: geogId,
        country: country,
        sortKey: ListingSortKeys.EvaId,
        sortDir: SortDir.Desc,
        locale: locale === 'fr-CA' ? 'fr' : locale,
        size: 6,
        page: 1,
      });
      setCardArr(listingsRes.data.listings);
    } catch (error) {
      setListingsError('Error fetching neighborhood listings');
      console.error(error);
    }
  };

  // Sets neighborhood listings for non local logic countries
  async function getAndSetNeighborhood(slug: string, shopIds: string[]) {
    try {
      const data = await apiClient.getNeighborhoodBySlug({ slug, shopIds });

      if (data) {
        const urlParameters = `?neighborhoodId=${data.data?.neighborhood_id}`;
        setNeighborhoodUrlParameters(urlParameters);
        const coordinates = data.data?.polygon?.coordinates?.[0]?.map(([lng, lat]) => ({
          lat: Number(lat),
          lng: Number(lng),
        }));

        if (coordinates) {
          setKmlPolygon(coordinates);
        }

        await start({
          action: async () => {
            let res: AxiosResponse<ListingSearchResult, any>;

            res = await apiClient.getListingsByNeighborhood({
              listingFilters: {
                shopIds: shopIds,
                listingStatuses: [StandardStatusEnum.Active],
              },
              id: data.data?.neighborhood_id,
              country: 'Other',
              sortKey: ListingSortKeys.EvaId,
              sortDir: SortDir.Desc,
              locale: locale === 'fr-CA' ? 'fr' : locale,
              size: 200,
              page: 1,
            });

            // Adjust duplicate coordinates before returning listings
            const seen = new Map();
            res.data.listings?.forEach((listing) => {
              if (!listing.Latitude || !listing.Longitude) {
                return;
              }

              // Create a unique key for each lat-lng pair
              let key = `${listing.Latitude},${listing.Longitude}`;

              // Check if the lat-lng pair has already been seen
              while (seen.has(key)) {
                // If duplicate, adjust the lat and lng slightly
                listing.Latitude += 0.00002; // Adjust by a very small increment
                listing.Longitude += 0.00002; // Adjust by a very small increment
                // Update the key to check again
                key = `${listing.Latitude},${listing.Longitude}`;
              }

              // Mark this lat-lng pair as seen
              seen.set(key, true);
            });

            setCardArr(res?.data?.listings);
            return { data: res?.data };
          },
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  const getDefaultContactUsProps = async () => {
    try {
      const data = await getDefaultNeighborhoodContactUsComponentData(locale);
      if (data) {
        setContactUsProps({
          ...data,
          fields: {
            ...data?.fields,
            chimeId: chimeData?.chimeId,
            chimeType: 'Shop',
          },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (localLogicId && localLogicCountry) {
      // Get listings for US & Canada local logic neighoborhoods
      fetchNeighborhoodData(localLogicId, localLogicCountry);
      fetchNeighborhoodListings(localLogicId, localLogicCountry);
    } else if (slug && shopIds && country !== 'United States' && country !== 'Canada') {
      const shopIdsArr = shopIds.split(',');
      // Get listings for non local logic countries
      getAndSetNeighborhood(slug, shopIdsArr);
    } else {
      console.error('ERROR: Missing slug for neighborhood');
    }
  }, []);

  useEffect(() => {
    if (neighborhoodCentroid && isLoaded) {
      geocodeLocation(neighborhoodCentroid.lat, neighborhoodCentroid.lng)
        .then((results) => {
          const geocode = results[0];

          // get city,state of the neighborhood and set it to the subtitle
          if (!fields?.subtitle) {
            const city = geocode?.address_components?.find(
              (component) => component.types[0] === 'locality',
            )?.long_name;
            const state = geocode?.address_components?.find(
              (component) => component.types[0] === 'administrative_area_level_1',
            )?.short_name;
            let location = city ? `${city}, ${state}` : state;
            setNeighborhoodSubtitle(location);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [neighborhoodCentroid, isLoaded]);

  useEffect(() => {
    if (!fields?.contact) {
      getDefaultContactUsProps();
    } else {
      setContactUsProps({
        ...fields?.contact,
        fields: {
          ...fields?.contact.fields,
          chimeId: chimeData?.chimeId,
          chimeType: 'Shop',
        },
      });
    }
  }, []);

  //
  //
  //Market Trends Component
  //
  // {
  //   zipCode &&
  //     countryCodeAPI === 'USA' &&
  //     components.push(
  //       <MarketInsights
  //         className="neighborhoods__market-details--wrapper"
  //         zipCode={zipCode}
  //         disclaimer={
  //           fields?.marketTrendsLegal ? fields.marketTrendsLegal : null
  //         }
  //         locale={locale ? locale : 'en'}
  //         currencyCode={
  //           currencyData?.currencyCode ? currencyData.currencyCode : 'USD'
  //         }
  //
  //       />,
  //     );
  // }

  //
  //
  //Newest Listings Component
  //
  let newestListingsCtaUrl = `${shopUrlPrepend}/properties`;
  if (neighborhoodUrlParameters) {
    newestListingsCtaUrl += neighborhoodUrlParameters;
    const urlEncodedNeighborhoodName = encodeURIComponent(fields?.neighborhood?.fields?.name);
    newestListingsCtaUrl += `&locationtitle=${urlEncodedNeighborhoodName}`;
  }

  const countryCode = fields?.neighborhood?.fields?.country
    ? fields?.neighborhood?.fields?.country === 'United States'
      ? 'US'
      : 'CA'
    : null;

  const updatedDynamicContentBlocks = addShopDataToContentBlocks({
    contentBlocks: fields?.dynamicContentBlocks,
    chimeId: chimeData?.chimeId,
    chimeType: 'Shop',
    contentfulTagIds: contentfulTagIds,
  });

  return (
    <main>
      {fields?.neighborhood && (
        <InteriorHero
          header={t(fields?.neighborhood?.fields?.name)}
          location={neighborhoodSubtitle}
          imgFocalPoint={fields?.neighborhood?.fields?.featuredImg}
          source="Image Upload"
        />
      )}

      {(neighborhoodStoryHeader || neighborhoodStoryBody) && (
        <IntroTextBlock
          header={t(neighborhoodStoryHeader ? neighborhoodStoryHeader : null)}
          richText={neighborhoodStoryBody ? neighborhoodStoryBody : null}
          swatch="cream"
        />
      )}

      {neighborhoodError && (
        <Typography color="error" textAlign="center">
          {t('NeighborhoodHomepage.neighborhoodError')}
        </Typography>
      )}

      {cardArr && cardArr.length > 0 && (
        <ShopsNewestListings
          newestListingsHeader={t(
            fields?.newestListingsHeader ? fields.newestListingsHeader : 'Newest Listings',
          )}
          neighborhoodCardArr={cardArr.slice(0, 6)}
          isNeighborhoodPage={true}
          ctaUrl={newestListingsCtaUrl}
        />
      )}

      {listingsError && (
        <Typography color="error" textAlign="center">
          {t('NeighborhoodHomepage.listingsError')}
        </Typography>
      )}

      {countryCode && neighborhoodCentroid && (
        <NeighborhoodLocalLogic
          header={t(fields?.mapHeader ? fields?.mapHeader : 'Get to know the neighborhood.')}
          coordinates={{
            lat: neighborhoodCentroid?.lat,
            lng: neighborhoodCentroid?.lng,
          }}
          countryCode={countryCode}
          geogId={localLogicId}
          locale={locale?.includes('fr') ? 'fr' : 'en'} // Only supports english and french locales
        />
      )}
      {!neighborhoodCentroid && listingData && listingData?.listings?.length > 0 && isLoaded && (
        <MapSection>
          <PropertyListingsMap
            listings={listingData?.listings || []}
            mapPropertyLoader={false}
            config={config}
            kmlPolygon={kmlPolygon || []}
            isNeighborhoodPage={true}
            zoomValue={8}
          />
        </MapSection>
      )}

      {updatedDynamicContentBlocks && updatedDynamicContentBlocks.length > 0 && (
        <MapComponents blocks={updatedDynamicContentBlocks} />
      )}

      {contactUsProps && <StackedTextImageForm {...contactUsProps.fields} />}
    </main>
  );
};

export default NeighborhoodHomepage;
